import { withNamespaces } from "react-i18next";
import React, { useState, memo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import Spinner from "react-bootstrap/Spinner";
import Breadcrumbs from 'src/components/Common/Breadcrumb';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { toast } from "react-toastify";
import DateUtils from "src/services/utils/DateUtils";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { Button, Col, Container, Row } from "reactstrap";
import {
    HistoryLogSections,
    HistoryLogRoles,
    HistoryLogActions,
} from "src/modules/data-protection/constants";
import { withRouter } from "react-router-dom";
import DataProtectionHistoryLogeService from "src/modules/data-protection/apis/DataProtectionHistoryLogService";
import DetailsModal from "../../components/history-log/details-modal";
import FilteringComponent from "../../components/history-log/filtering-component";

const DataProtectionHistoryLog = ({ t, assetId }) => {
    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        fromDate: null,
        toDate: null,
        user: [],
        roles: [],
        sections: [],
        actions: [],
    });

    const [selectedLog, setSelectedLog] = useState(null);

    const dateUtils = new DateUtils();

    const tableColumns = [
        {
            dataField: "secondary_id",
            text: "#",
            sort: false,
            key: 1,
            style: {
                width: 120,
            },
        },
        {
            dataField: "action",
            text: t("Activity"),
            sort: false,
            key: 2,
            style: {
                width: 180,
            },
            formatter: (cellContent, row) => {
                return t(HistoryLogSections[row.section]);
            },
        },
        {
            dataField: "date_time",
            text: t("Date and time"),
            sort: false,
            key: 3,
            style: {
                width: 180,
            },
            formatter: (cellContent, row) => {
                return (
                    <div>
                        <span>
                            {dateUtils.convertDateToDate(
                                row.created_at,
                                INTERNATIONAL_DATE_FORMAT
                            )}
                        </span>
                        <br />
                        <span className="text-muted">
                            {dateUtils.convertDateToDate(row.created_at, "HH:mm")}
                        </span>
                    </div>
                );
            },
        },
        {
            dataField: "user_name",
            text: t("User"),
            sort: false,
            key: 4,
            style: {
                width: 180,
            },
        },
        {
            dataField: "user_role",
            text: t("User Role"),
            sort: false,
            key: 5,
            style: {
                width: 180,
            },
            formatter: (cellContent, row) => {
                return t(HistoryLogRoles[row.user_role]);
            },
        },
        {
            dataField: "description",
            text: t("Description"),
            sort: false,
            key: 6,
            formatter: (cellContent, row) => {
                const section = HistoryLogActions[row.section];

                return t(section ? section[row.action] : "");
            },
        },
        {
            dataField: "actions",
            text: t("Action"),
            sort: false,
            key: 7,
            formatter: (cellContent, row) => {
                return (
                    <div className="text-danger text-truncate text-capitalize text-center d-flex justify-content-start">
                        <Button
                            color="primary"
                            onClick={() => setSelectedLog(row)}
                            outline
                            className="border-0"
                        >
                            <i className="ri-eye-line font-size-20"></i>
                        </Button>
                    </div>
                );
            },
        },
    ];

    const breadcrumbItems = [
        { title: 'SIP', link: '/' },
        { title: t('History Logs'), link: '#' },
    ];

    const NoDataIndication = () =>
        handleFetchHistoryLogQuery.isFetched &&
            !handleFetchHistoryLogQuery.length ? (
            <div className="alert m-0" role="alert">
                <p
                    style={{
                        textAlign: "center",
                        marginBottom: 0,
                    }}
                >
                    {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleFetchHistoryLogQuery = useQuery({
        queryKey: [
            "data-protection-history-logs",
            tableFilters
        ],
        queryFn: async () => {
            const service = DataProtectionHistoryLogeService.getInstance();
            return await service.fetchLogs(tableFilters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("Failed to fetch history logs."), {
                type: "error",
            });
        },
    });

    const handleTableChange = (type, 
        { 
            page, 
            sizePerPage,
            sortField,
            sortOrder
        }) => {
        switch (type) {
            case "pagination":
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage
                });
                break;
            case 'sort':
                // setTableFilters({
                //     ...tableFilters,
                //     sortField: sortField,
                //     sortOrder: sortOrder.toUpperCase()
                // })
                break;
            default:
                return false;
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        setTableFilters((prevFilters) => ({...prevFilters, ...newFilters}));
    }, []);

    return (
        <React.Fragment>
            <div className='page-content'>
                <Container fluid>
                    <Breadcrumbs title={t('History Logs')} breadcrumbItems={breadcrumbItems} />
                    <Row className="p-4">
                        <Col sm="12">
                            <div
                                className="bg-white"
                                style={{
                                    borderRadius: "7px",
                                    boxShadow: "0px 0px 4px 0px #4F666E40",
                                    marginBottom: "40px",
                                }}
                            >
                                <div className="p-4" style={{ marginBottom: "300px" }}>
                                    <PaginationProvider
                                        pagination={paginationFactory({
                                            custom: true,
                                            page: tableFilters.pageIndex,
                                            sizePerPage: tableFilters.pageSize,
                                            totalSize: handleFetchHistoryLogQuery.data?.itemsCount || 0,
                                            withFirstAndLast: false,
                                            alwaysShowAllBtns: true,
                                            prePageText: (
                                                <span>
                                                    <i className="ri-arrow-left-s-line"></i> {t("Back")}
                                                </span>
                                            ),
                                            nextPageText: (
                                                <span>
                                                    {t("Next")} <i className="ri-arrow-right-s-line"></i>
                                                </span>
                                            ),
                                            prePageTitle: t("Pre page"),
                                            firstPageTitle: t("Next page"),
                                            showTotal: true,
                                            hideSizePerPage: false,
                                            sizePerPageList: [
                                                {
                                                    text: "25",
                                                    value: 25,
                                                },
                                                {
                                                    text: "50",
                                                    value: 50,
                                                },
                                            ],
                                        })}
                                    >
                                        {({ paginationProps, paginationTableProps }) => {
                                            return (
                                                <React.Fragment>
                                                    <Row className="mb-4">
                                                        <Col sm="12">
                                                            <div className="d-flex gap-2 justify-content-end">
                                                                <FilteringComponent 
                                                                    onChange={handleFilterChange}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col sm="12">
                                                            <BootstrapTable
                                                                remote={{
                                                                    pagination: true,
                                                                    filter: false,
                                                                    sort: true,
                                                                    cellEdit: false,
                                                                    search: false,
                                                                }}
                                                                loading={
                                                                    handleFetchHistoryLogQuery.isFetching ||
                                                                    handleFetchHistoryLogQuery.isLoading
                                                                }
                                                                overlay={overlayFactory({
                                                                    spinner: (
                                                                        <Spinner
                                                                            animation="border"
                                                                            variant="primary"
                                                                            size="md"
                                                                        />
                                                                    ),
                                                                    text: "Loading...",
                                                                })}
                                                                onTableChange={handleTableChange}
                                                                defaultSorted={[]}
                                                                keyField={"id"}
                                                                responsive
                                                                bordered={false}
                                                                data={handleFetchHistoryLogQuery.data?.logs || []}
                                                                striped={false}
                                                                columns={tableColumns}
                                                                wrapperClasses="table-responsive"
                                                                classes={"table tpdd-table"}
                                                                headerWrapperClasses={"thead-light"}
                                                                style={{
                                                                    overflowX: "auto",
                                                                }}
                                                                noDataIndication={() => <NoDataIndication />}
                                                                {...paginationTableProps}
                                                            />
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col sm="12" md="6">
                                                            <div className="tpdd-pagination-style-1">
                                                                <PaginationListStandalone {...paginationProps} />

                                                                <SizePerPageDropdownStandalone {...paginationProps} />
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </React.Fragment>
                                            )
                                        }}
                                    </PaginationProvider>
                                </div>
                            </div>
                            {selectedLog && (
                                <DetailsModal
                                    logId={selectedLog.id}
                                    onClose={() => setSelectedLog(null)}
                                />
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default withNamespaces()(withRouter(memo(DataProtectionHistoryLog)));
